import { get, post } from './common';
import {
	FormData,
	FormSubmission,
	FormSubmissionResult,
	LegacyApiForm,
	LegacyFormSubmitResponse,
	FormSubmitResult,
} from '../Types/forms';
import { normalizeFormData } from '../Types/formNormalizer';

const userEndpoint = 'api/FormsUser';
const adminEndpoint = 'api/FormsAdmin';

// ---------------------------------------------------------------------------
// User endpoints
// ---------------------------------------------------------------------------

/**
 * Fetches a form for user display/submission by its formKey (GUID).
 */
export async function getFormForUser(formKey: string): Promise<FormData> {
	const result = await get<LegacyApiForm>(`${userEndpoint}/GetForm?guid=${encodeURIComponent(formKey)}`);
	return normalizeFormData(result!);
}

/**
 * Submits a completed form.
 */
export async function submitForm(submission: FormSubmission): Promise<FormSubmitResult> {
	const result = await post<LegacyFormSubmitResponse>(`${userEndpoint}/SubmitForm`, submission);
	if (!result || result.Success !== true) {
		const message = typeof result?.Message === 'string' && !result.Message.startsWith('action:') ? result.Message : '';
		throw new Error(message || 'Failed to submit form. Please try again.');
	}
	return {
		success: true,
		isPassed: typeof result.ExtraInfo?.IsPassed === 'boolean' ? result.ExtraInfo.IsPassed : undefined,
	};
}

/**
 * Checks whether the current user has already submitted a specific form.
 */
export async function hasUserSubmittedForm(formId: number): Promise<boolean> {
	const result = await get<boolean>(`${userEndpoint}/HasUserSubmittedForm?formId=${formId}`);
	return result ?? false;
}

/**
 * Validates a reCAPTCHA response token.
 */
export async function isCaptchaValid(encodedResponse: string): Promise<boolean> {
	const result = await get<boolean>(`${userEndpoint}/IsCaptchaValid?encodedResponse=${encodeURIComponent(encodedResponse)}`);
	return result ?? false;
}

// ---------------------------------------------------------------------------
// Admin endpoints
// ---------------------------------------------------------------------------

/**
 * Fetches a form for administration by its formKey (GUID).
 */
export async function getFormForAdmin(formKey: string): Promise<FormData> {
	const result = await get<LegacyApiForm>(`${adminEndpoint}/GetForm?guid=${encodeURIComponent(formKey)}`);
	return normalizeFormData(result!);
}

/**
 * Saves a form (create or update).
 * Accepts the form in the legacy PascalCase format expected by the backend.
 */
export async function saveForm(form: Record<string, unknown>): Promise<FormData> {
	const result = await post<LegacyApiForm>(`${adminEndpoint}/SaveForm`, form);
	return normalizeFormData(result!);
}

/**
 * Gets formatted submissions for a form.
 */
export async function getFormSubmissions(formId: number, startDate?: string, endDate?: string): Promise<FormSubmissionResult[]> {
	let endpoint = `${adminEndpoint}/GetFormSubmissions?formId=${formId}`;
	if (startDate) endpoint += `&startDate=${encodeURIComponent(startDate)}`;
	if (endDate) endpoint += `&endDate=${encodeURIComponent(endDate)}`;
	const result = await get<FormSubmissionResult[]>(endpoint);
	return result ?? [];
}

/**
 * Copies a form, creating a new form with the same fields and settings.
 */
export async function copyForm(formId: number): Promise<FormData> {
	const result = await post<LegacyApiForm>(`${adminEndpoint}/CopyForm`, { FormId: formId });
	return normalizeFormData(result!);
}

/**
 * Archives a single submission.
 */
export async function archiveSubmission(submissionId: number): Promise<void> {
	await post(`${adminEndpoint}/ArchiveSubmission`, { SubmissionId: submissionId });
}

/**
 * Fetches available form plugins.
 */
export async function getPlugins(): Promise<unknown[]> {
	const result = await get<unknown[]>(`${userEndpoint}/GetPlugins`);
	return result ?? [];
}
